import React, { createContext, useCallback, useContext, useEffect, useState } from "react";
import { fetchMe, logout as apiLogout } from "@/lib/auth";

const AuthContext = createContext({
  user: null,
  meta: null,
  loading: true,
  refresh: async () => {},
  logout: async () => {},
});

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [meta, setMeta] = useState(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchMe();
      if (data?.user) {
        setUser(data.user);
        setMeta(data.meta || null);
      } else {
        setUser(null);
        setMeta(null);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    refresh();
  }, [refresh]);

  const logout = useCallback(async () => {
    try {
      await apiLogout();
    } finally {
      setUser(null);
      setMeta(null);
      // Privacy fix: after logout, every cached page/component holding a
      // user's report data must be remounted so it can't keep rendering
      // private numbers under a "Sign In" header. Sending the user to "/"
      // also detaches them from any /dashboard/:reportId URL that an
      // unauthenticated visitor could otherwise revisit by hitting Back.
      if (typeof window !== "undefined") {
        window.location.assign("/");
      }
    }
  }, []);

  return (
    <AuthContext.Provider value={{ user, meta, loading, refresh, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
